/**
 * Razorpay Payment Integration Service
 * Handles payment processing for package purchases
 * Connected to Firebase Cloud Functions backend
 */

// Cloud Function URLs — set in .env
// TEST  (harshal-jobs project)      → REACT_APP_CREATE_ORDER_URL
// PROD  (job-platform-906af project) → fallback hardcoded below
const CREATE_ORDER_URL = process.env.REACT_APP_CREATE_ORDER_URL
  || "https://createrazorpayorder-54zvx2qstq-uc.a.run.app";

const VERIFY_PAYMENT_URL = process.env.REACT_APP_VERIFY_PAYMENT_URL
  || "https://verifyrazorpaypayment-54zvx2qstq-uc.a.run.app";

// Razorpay configuration — reads from .env
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_SPs6AqG8E3r2Cp';

console.log(`🔧 [RazorpayService] Create Order URL: ${CREATE_ORDER_URL}`);
console.log(`🔧 [RazorpayService] Verify Payment URL: ${VERIFY_PAYMENT_URL}`);
console.log(`🔧 [RazorpayService] Key ID: ${RAZORPAY_KEY_ID}`);

/**
 * Load Razorpay script dynamically
 * @returns {Promise<boolean>} Success status
 */
export const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/** 
 * Create Razorpay order via server endpoint
 * In dev: calls local proxy (setupProxy.js) → Razorpay API
 * In production: calls Cloud Function → Razorpay API
 * @param {Object} orderData - Order details
 * @returns {Object} Order creation result
 */
export const createRazorpayOrder = async (orderData) => {
  try {
    console.log('💳 [createRazorpayOrder] Creating order for amount:', orderData.amount);

    // Payload format expected by both Cloud Function and local proxy
    const payload = {
      orderId: orderData.receipt || `order_${Date.now()}`,
      orderAmount: orderData.amount, // Amount in INR (server multiplies by 100)
      orderCurrency: orderData.currency || 'INR',
      customerId: orderData.notes?.userId || 'guest',
      customerEmail: orderData.notes?.userEmail,
      customerPhone: orderData.notes?.userPhone
    };

    console.log('🔐 [createRazorpayOrder] Calling:', CREATE_ORDER_URL);
    console.log('📝 [createRazorpayOrder] Payload:', JSON.stringify(payload, null, 2));

    const response = await fetch(CREATE_ORDER_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    console.log('📥 [createRazorpayOrder] Response:', data);

    if (!response.ok) {
      console.error('❌ [createRazorpayOrder] Failed:', data);
      const errorMsg = data.details?.description || data.error || `HTTP ${response.status}`;
      throw new Error(`Order creation failed: ${errorMsg}`);
    }

    return {
      success: true,
      order: {
        id: data.orderId,
        amount: data.amount,
        currency: data.currency
      }
    };

  } catch (error) {
    console.error('❌ [createRazorpayOrder] Error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Initialize Razorpay payment
 * @param {Object} paymentData - Payment configuration
 * @returns {Promise<Object>} Payment result
 */
export const initiateRazorpayPayment = async (paymentData) => {
  try {
    console.log('💳 [initiateRazorpayPayment] Starting payment process...');

    const {
      amount,
      currency = 'INR',
      packageName,
      packageId,
      userEmail,
      userName,
      userPhone,
      employerName,
      employerPhone,
      onSuccess,
      onFailure,
      userId
    } = paymentData;

    // Load Razorpay script
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      throw new Error('Failed to load Razorpay SDK. Please check your internet connection.');
    }

    // Create order
    const orderResult = await createRazorpayOrder({
      amount,
      currency,
      receipt: `pkg_${packageId}_${Date.now()}`,
      notes: {
        packageId,
        packageName,
        userId,
        userEmail,
        userPhone
      },
    });

    if (!orderResult.success) {
      throw new Error(orderResult.error || 'Failed to create payment order.');
    }

    const order = orderResult.order;
    console.log('✅ [initiateRazorpayPayment] Order created:', order.id);

    const options = {
      key: RAZORPAY_KEY_ID,
      amount: order.amount,
      currency: order.currency,
      name: 'Job Platform',
      description: `Purchase ${packageName}`,
      image: '/logo.png',
      order_id: order.id,
      prefill: {
        name: userName,
        email: userEmail,
        contact: userPhone,
      },
      theme: { color: '#4F46E5' },
      handler: async function (response) {
        console.log('✅ [Razorpay] Payment successful at client!');
        try {
          // Verify
          const verificationResult = await verifyPaymentSignature({
            orderId: response.razorpay_order_id,
            paymentId: response.razorpay_payment_id,
            signature: response.razorpay_signature,
          });

          if (verificationResult.success && verificationResult.verified) {
            console.log('✅ [Razorpay] Verified successfully');
            if (onSuccess) {
              onSuccess({
                ...response,
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                signature: response.razorpay_signature
              });
            }
          } else {
            throw new Error(verificationResult.error || 'Verification failed');
          }
        } catch (verifyError) {
          console.error('❌ [Razorpay] Verification error:', verifyError);
          if (onFailure) onFailure({ error: verifyError.message });
        }
      },
      modal: {
        ondismiss: function () {
          if (onFailure) onFailure({ cancelled: true, error: 'Cancelled by user' });
        },
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
    return { success: true, message: 'Payment initiated' };

  } catch (error) {
    console.error('❌ [initiateRazorpayPayment] Error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Verify payment signature via Firebase Cloud Function
 * @param {Object} paymentData - { orderId, paymentId, signature }
 * @returns {Promise<Object>} Verification result
 */
export const verifyPaymentSignature = async (paymentData) => {
  try {
    console.log('🔐 [verifyPaymentSignature] Verifying:', paymentData);
    console.log('🔐 [verifyPaymentSignature] Using URL:', VERIFY_PAYMENT_URL);

    const response = await fetch(VERIFY_PAYMENT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orderId: paymentData.orderId,
        paymentId: paymentData.paymentId,
        signature: paymentData.signature,
      }),
    });

    const data = await response.json();
    console.log('✅ [verifyPaymentSignature] Response:', data);

    // Function returns: isPaid: boolean
    const verified = data.isPaid || data.status === 'captured';

    if (!response.ok || !verified) {
      throw new Error(data.error || 'Verification failed on server');
    }

    return { success: true, verified: true, data };
  } catch (error) {
    console.error('❌ [verifyPaymentSignature] Error:', error);
    return { success: false, error: error.message, verified: false };
  }
};

/**
 * Send WhatsApp message - Placeholder or use generic
 */
export const sendWhatsAppMessage = async (messageData) => {
  // Basic implementation or placeholder if no specific URL provided for this
  console.log('WhatsApp message feature not fully configured with new functions', messageData);
  return { success: true };
};

export const getPaymentDetails = async (paymentId) => {
  // Not implemented in provided functions list
  return { success: false, error: 'Not implemented' };
};

export const formatAmount = (amount, currency = 'INR') => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const calculateGST = (amount) => {
  const gstRate = 0.18;
  const gstAmount = amount * gstRate;
  const totalAmount = amount + gstAmount;

  return {
    baseAmount: amount,
    gstRate: gstRate * 100,
    gstAmount: Math.round(gstAmount * 100) / 100,
    totalAmount: Math.round(totalAmount * 100) / 100,
  };
};

export default {
  loadRazorpayScript,
  createRazorpayOrder,
  initiateRazorpayPayment,
  verifyPaymentSignature,
  sendWhatsAppMessage,
  getPaymentDetails,
  formatAmount,
  calculateGST,
};
